// src/components/TopBar.jsx
import React, { useContext, useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { AuthContext } from "../contexts/AuthContext";
import { VERSION } from "../version";
import NewsAssistantLogo from "./NewsAssistantLogo";
import { FeedbackModal } from "./FeedbackModal";
import NewsSourcesSelector from "./NewsSourcesSelector";
import PersonalInterestModal from "./PersonalInterestModal";
import MemoriesModal from "./MemoriesModal";

export default function TopBar() {
  const {
    user,
    chatStyle,
    allowedDomains,
    personalInterest,
    updateChatStyle,
    updateAllowedDomains,
  } = useContext(AuthContext);

  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showPersonalInterestModal, setShowPersonalInterestModal] =
    useState(false);
  const [showMemoriesModal, setShowMemoriesModal] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [feedbackError, setFeedbackError] = useState("");
  const [feedbackSuccess, setFeedbackSuccess] = useState("");
  const accountDropdownRef = useRef(null);

  const handleLogout = async () => {
    console.log("Logging out...");
    await supabase.auth.signOut();
  };

  // Desktop: Close account dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        accountDropdownRef.current &&
        !accountDropdownRef.current.contains(e.target)
      ) {
        setShowAccountDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handler for submitting feedback
  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    setFeedbackError("");
    if (!feedback.trim()) {
      setFeedbackError("Please enter your feedback.");
      return;
    }
    const { error } = await supabase.from("feedback").insert([
      {
        feedback,
        created_at: new Date(),
        user_id: user ? user.id : null,
      },
    ]);
    if (error) {
      setFeedbackError(
        "There was an error submitting your feedback. Please try again."
      );
    } else {
      setFeedbackSuccess("Thank you for your feedback!");
      setFeedback("");
      setTimeout(() => {
        setFeedbackSuccess("");
        setShowFeedbackModal(false);
      }, 2000);
    }
  };

  const closeFeedbackModal = () => {
    setShowFeedbackModal(false);
    setFeedbackError("");
    setFeedback("");
  };

  // Predefined list of domain options.
  const domainOptions = [
    "techcrunch.com",
    "theverge.com",
    "wired.com",
    "apnews.com",
    "bbc.com",
  ];

  return (
    <>
      {/* Modals */}
      {showPersonalInterestModal && (
        <PersonalInterestModal
          personalInterest={personalInterest}
          closeModal={() => setShowPersonalInterestModal(false)}
        />
      )}
      {showMemoriesModal && user && (
        <MemoriesModal
          user={user}
          closeModal={() => setShowMemoriesModal(false)}
        />
      )}
      {showFeedbackModal && (
        <FeedbackModal
          feedback={feedback}
          setFeedback={setFeedback}
          feedbackError={feedbackError}
          feedbackSuccess={feedbackSuccess}
          handleSubmitFeedback={handleSubmitFeedback}
          closeModal={closeFeedbackModal}
        />
      )}
      <header className="relative z-50 w-full bg-white bg-opacity-90 backdrop-blur-md border-b border-gray-200 shadow-md">
        <div className="mx-auto px-4">
          {/* Main Header Row */}
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center space-x-3">
              <div className="w-14 h-14">
                <NewsAssistantLogo />
              </div>
              <Link to="/" className="hover:opacity-80 transition-opacity">
                <div className="text-xl font-semibold tracking-tight text-gray-800">
                  Twipe News Buddy{" "}
                  <span className="text-sm font-light">{VERSION}</span>
                </div>
              </Link>
            </div>

            {/* Desktop Controls */}
            <div className="hidden md:flex items-center space-x-4">
              {user ? (
                <div className="relative" ref={accountDropdownRef}>
                  <button
                    onClick={() => setShowAccountDropdown(!showAccountDropdown)}
                    className="px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-800 rounded-full shadow-sm transition-colors"
                    aria-label="User Account"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5.121 17.804A9.004 9.004 0 0112 15c2.21 0 4.21.894 5.879 2.337M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </button>
                  {showAccountDropdown && (
                    <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 z-50">
                      <div className="border-b border-gray-200 pb-2 mb-2">
                        <p className="font-medium text-gray-800">
                          {user.email}
                        </p>
                      </div>
                      <div className="mb-2">
                        <label
                          htmlFor="chatStyleSelectDesktop"
                          className="block text-gray-600 text-sm"
                        >
                          Chat Style:
                        </label>
                        <div className="relative mt-1">
                          <select
                            id="chatStyleSelectDesktop"
                            value={chatStyle}
                            onChange={(e) => updateChatStyle(e.target.value)}
                            className="block appearance-none w-full border border-gray-300 rounded-full px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                          >
                            <option value="short">Short &amp; Snappy</option>
                            <option value="insights">
                              Insights (Structured)
                            </option>
                            <option value="realistic">Real Conversation</option>
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <svg
                              className="h-4 w-4 text-gray-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                      {/* <div className="mb-2">
                        <label className="block text-gray-600 text-sm mb-1">News Sources:</label>
                        <NewsSourcesSelector
                          options={domainOptions}
                          selected={allowedDomains}
                          onChange={updateAllowedDomains}
                        />
                      </div> */}
                      {/* <div className="mb-2">
                        <button
                          onClick={() => {
                            setShowPersonalInterestModal(true);
                            setShowAccountDropdown(false);
                          }}
                          className="w-full text-left py-1 hover:bg-gray-50 rounded transition-colors"
                        >
                          View Personal Interest
                        </button>
                      </div>
                      <div className="mb-2">
                        <button
                          onClick={() => {
                            setShowMemoriesModal(true);
                            setShowAccountDropdown(false);
                          }}
                          className="w-full text-left py-1 hover:bg-gray-50 rounded transition-colors"
                        >
                          View Memories
                        </button>
                      </div> */}
                      <button
                        onClick={() => {
                          setShowFeedbackModal(true);
                          setShowAccountDropdown(false);
                        }}
                        className="w-full text-left py-1 hover:bg-gray-50 rounded transition-colors"
                      >
                        Feedback
                      </button>
                      <button
                        onClick={() => {
                          handleLogout();
                          setShowAccountDropdown(false);
                        }}
                        className="w-full text-left py-1 hover:bg-gray-50 rounded transition-colors"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button className="px-4 py-2 rounded-full bg-green-600 text-white shadow hover:bg-green-700 transition-colors">
                  Login
                </button>
              )}
            </div>

            {/* Mobile Hamburger */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-md text-gray-800 hover:bg-gray-100 focus:outline-none focus:ring-0"
                aria-label="Toggle menu"
              >
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {mobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 pt-2 pb-3">
              {user ? (
                <div className="flex flex-col space-y-2 px-4">
                  {/* Display email at the top */}
                  <div className="border-b border-gray-200 pb-2 mb-2">
                    <p className="font-medium text-gray-800">{user.email}</p>
                  </div>
                  {/* <div className="flex flex-col">
                    <label htmlFor="newsSourcesSelectorMobile" className="text-gray-600 text-sm mb-1">
                      News Sources:
                    </label>
                    <NewsSourcesSelector
                      id="newsSourcesSelectorMobile"
                      options={domainOptions}
                      selected={allowedDomains}
                      onChange={updateAllowedDomains}
                    />
                  </div> */}
                  <div className="mb-2">
                    <label
                      htmlFor="chatStyleSelectMobile"
                      className="block text-gray-600 text-sm"
                    >
                      Chat Style:
                    </label>
                    <div className="relative mt-1">
                      <select
                        id="chatStyleSelectMobile"
                        value={chatStyle}
                        onChange={(e) => updateChatStyle(e.target.value)}
                        className="block appearance-none w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                      >
                        <option value="short">Short &amp; Snappy</option>
                        <option value="insights">Insights (Structured)</option>
                        <option value="realistic">Real Conversation</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <svg
                          className="h-4 w-4 text-gray-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  {/* <button
                    onClick={() => {
                      setShowPersonalInterestModal(true);
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left py-1 hover:bg-gray-50 rounded transition-colors text-gray-800"
                  >
                    View Personal Interest
                  </button>
                  <button
                    onClick={() => {
                      setShowMemoriesModal(true);
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left py-1 hover:bg-gray-50 rounded transition-colors text-gray-800"
                  >
                    View Memories
                  </button> */}
                  <button
                    onClick={() => {
                      setShowFeedbackModal(true);
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left py-1 hover:bg-gray-50 rounded transition-colors text-gray-800"
                  >
                    Feedback
                  </button>
                  {/* <Link
                    to="/Newsmatcher"
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-full text-left py-1 text-gray-800 hover:bg-gray-50 rounded transition-colors"
                  >
                    Newsmatcher
                  </Link> */}
                  {/* Standalone Logout Button */}
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left py-1 hover:bg-gray-50 rounded transition-colors text-gray-800"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <button className="w-full px-4 py-2 rounded-full bg-green-600 text-white shadow hover:bg-green-700 transition-colors">
                  Login
                </button>
              )}
            </div>
          )}
        </div>
      </header>
    </>
  );
}
